'use client';

import LazyLoadingWrapper from '@/components/common/LazyLoadingWrapper';
import HeroSection from '@/components/home/HeroSection';
import { useLazyLoad } from '@/hooks/useLazyLoad';
import { RootState } from '@/store';
import { HomeData } from '@/types';
import dynamic from 'next/dynamic';
import Head from 'next/head';
import { useSelector } from 'react-redux';

// Dynamic imports
const Banner1 = dynamic(() => import('@/components/home/Banner1'));
const OffersImageSliders = dynamic(
  () => import('@/components/home/OffersImageSliders')
);
const Categories = dynamic(() => import('@/components/home/Categories'));
const Banner2 = dynamic(() => import('@/components/home/Banner2'));
const ProductOffers = dynamic(() => import('@/components/home/ProductOffers'));
const FeaturedProducts = dynamic(
  () => import('@/components/home/FeaturedProducts')
);
const Articles = dynamic(() => import('@/components/home/Articles'));
const FAQs = dynamic(() => import('@/components/home/FAQs'));
const NewArrivals = dynamic(() => import('@/components/home/NewArrivals'));
const FreeShippingProducts = dynamic(
  () => import('@/components/home/FreeShippingProducts')
);

interface HomeClientProps {
  homeData: HomeData;
}

export default function HomeClient({ homeData }: HomeClientProps) {
  const sections: any = {
    hero: {
      Component: HeroSection,
      params: { productSliders: homeData?.product_sliders },
      loading: useLazyLoad(),

      hasPadding: false,
    },
    categories: {
      Component: Categories,
      params: {},
      loading: useLazyLoad(),
    },
    banner_1: {
      Component: Banner1,
      params: {},
      loading: useLazyLoad(),
      hasPadding: false,
    },
    banner_2: {
      Component: Banner2,
      params: {},
      loading: useLazyLoad(),
      hasPadding: false,
    },
    offers: {
      Component: ProductOffers,
      params: { productsList: homeData?.products_offers },
      loading: useLazyLoad(),
    },
    featured_products: {
      Component: FeaturedProducts,
      params: {
        productsList: homeData?.featured_products,
      },
      loading: useLazyLoad(),
    },
    articles: {
      Component: Articles,
      params: { articlesList: homeData?.articles },
      loading: useLazyLoad(),
    },
    faqs: {
      Component: FAQs,
      params: { faqs: homeData?.faqs },
      loading: useLazyLoad(),
    },
    new_arrivals: {
      Component: NewArrivals,
      params: { productsList: homeData?.products_new_arrivals },
      loading: useLazyLoad(),
    },
    offers_slider: {
      Component: OffersImageSliders,
      params: { sliders: homeData?.sliders },
      loading: useLazyLoad(),
      hasPadding: false,
    },
    products_has_free_shipping: {
      Component: FreeShippingProducts,
      params: { productsList: homeData?.products_has_free_shipping },
      loading: useLazyLoad(),
    },
  };

  const { landing_sections, general } = useSelector(
    (state: RootState) => state.settings
  );

  return (
    <>
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: (general?.home_sections_gap_size ?? 16) + 'px',
        }}
      >
        {landing_sections.map((section) => {
          if (sections[section.key]) {
            const { Component, params, loading, initial, hasPadding } = sections[section.key];
          
            return (
              <LazyLoadingWrapper
                key={section.id}
                loading={loading}
                initial={initial}
                hasPadding={hasPadding}
              >
                <Component {...params} />
              </LazyLoadingWrapper>
            );
          }
        })}
      </div>
    </>
  );
}
